import React from 'react';
import { captureException } from '@sentry/react';
import Head from 'next/head';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { getSingleArticle } from 'data/SingleArticle/data';
import { Article as ArticleType } from 'data/SingleArticle/types';
import Article from 'components/pages/Article/Article';
import { Meta } from 'components/Meta';
import { PreviewModeBanner } from 'components/PreviewModeBanner/PreviewModeBanner';
import { PageWrapper } from 'components/PageWrapper/PageWrapper';
import { getAllSlugs } from 'data/SingleArticle/getAllSlugs';
import { Breadcrumbs } from 'components/BreadCrumbs/Breadcrumbs';
import { ArticleJsonLd } from 'components/JsonLd/ArticleJsonLd';
import { ZapierProductModule } from 'components/ZapierProductModule/ZapierProductModule';
import { Spacer } from 'components/Spacer/Spacer';
import { RelatedArticles } from 'components/RelatedArticles/RelatedArticles';
import { Spinner } from '@zapier/design-system';
import styled from '@emotion/styled';

type Props = {
  article: ArticleType;
  preview: boolean;
};

export default function BlogPost(props: Props) {
  const router = useRouter();

  // This displays only while getStaticProps() requests data after which the EmptyArticlePage is rendered
  // This will rarely _ever_ be seen (even then only for a short flash) thanks to prefetch and complete getStaticPaths()
  // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
  if (router.isFallback) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }
  // Happy path from here on forward. Display article page!
  const { article, preview } = props;
  const { metadata, relatedArticles, image, discoverable } = article;

  return (
    <>
      <script
        type="text/javascript"
        src="//s7.addthis.com/js/300/addthis_widget.js#pubid=ra-5f36e19bf3803e41"
      />
      <PageWrapper>
        <Meta
          title={metadata.seoTitle}
          description={metadata.seoDescription}
          image={metadata.socialImage?.image?.url || image.url}
        />
        <Head>
          {!discoverable && <meta name="robots" content="noindex" />}
          <ArticleJsonLd article={article} />
        </Head>
        {preview && <PreviewModeBanner />}
        <Breadcrumbs breadcrumbs={article.categories} />
        <Article article={article} />
        {relatedArticles && relatedArticles.length > 0 ? (
          <RelatedArticles articles={relatedArticles} />
        ) : null}
        <Spacer hasLine={false} />
      </PageWrapper>
      <ZapierProductModule />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const articles = await getAllSlugs();

  return {
    paths: articles.map(({ slug }) => ({ params: { slug } })),
    // Allow paths which aren't declared statically
    // This enables new blog posts to be added and supports previewing unpublished posts
    // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<Props, any> = async (context) => {
  const { slug } = context.params;
  const { preview = false } = context;

  let article: ArticleType;
  try {
    article = await getSingleArticle(slug, preview);
  } catch (e) {
    captureException(e);
    return {
      notFound: true,
    };
  }

  // This threshold can be dynamic even accounting for article publish date, category, etc...
  const THIRTY_MINUTES = 30 * 60;
  return {
    props: {
      article,
      preview,
    },
    // Periodically revalidate content and rebuild page if content has changed
    // https://nextjs.org/docs/basic-features/data-fetching#incremental-static-regeneration
    revalidate: THIRTY_MINUTES,
  };
};

const SpinnerContainer = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
